import { memo, useMemo, useCallback } from 'react';
import { GripVertical } from 'lucide-react';
import { WorkflowBlock, BlockGroup, BlockDimensions } from '@/types/workflow';
import { cn } from '@/lib/utils';
import { GRID_SIZE_PX, BLOCK_WIDTH_PX, BLOCK_HEIGHT_PX } from '@/constants/canvas';

interface GroupFrameProps {
  group: BlockGroup;
  blocks: WorkflowBlock[];
  blockDimensions: Record<string, BlockDimensions>;
  isSelected?: boolean;
  onClick?: () => void;
  /** Drag-start: tar emot Mouse- eller Pointer-event (PointerEvent
   *  extends MouseEvent strukturellt — clientX/Y, target etc. finns
   *  i båda). Mobil-pathen kallar med PointerEvent. */
  onDragStart?: (e: React.MouseEvent | React.PointerEvent) => void;
}

export const GroupFrame = memo(function GroupFrame({ group, blocks, blockDimensions, isSelected, onClick, onDragStart }: GroupFrameProps) {
  const groupBlocks = blocks.filter(b => group.blockIds.includes(b.id));

  const bounds = useMemo(() => {
    if (groupBlocks.length === 0) return null;

    const padding = GRID_SIZE_PX; // 20px — aligns to grid
    const labelHeight = GRID_SIZE_PX * 2; // 40px — aligns to grid

    // block.x/y = center of block
    const rawMinX = Math.min(...groupBlocks.map(b => {
      const dims = blockDimensions[b.id] || { width: BLOCK_WIDTH_PX, height: BLOCK_HEIGHT_PX };
      return b.x - dims.width / 2;
    })) - padding;
    const rawMinY = Math.min(...groupBlocks.map(b => {
      const dims = blockDimensions[b.id] || { width: BLOCK_WIDTH_PX, height: BLOCK_HEIGHT_PX };
      return b.y - dims.height / 2;
    })) - padding - labelHeight;
    const rawMaxX = Math.max(...groupBlocks.map(b => {
      const dims = blockDimensions[b.id] || { width: BLOCK_WIDTH_PX, height: BLOCK_HEIGHT_PX };
      return b.x + dims.width / 2;
    })) + padding;
    const rawMaxY = Math.max(...groupBlocks.map(b => {
      const dims = blockDimensions[b.id] || { width: BLOCK_WIDTH_PX, height: BLOCK_HEIGHT_PX };
      return b.y + dims.height / 2;
    })) + padding;

    // Snap group frame bounds to grid so everything lines up
    const minX = Math.floor(rawMinX / GRID_SIZE_PX) * GRID_SIZE_PX;
    const minY = Math.floor(rawMinY / GRID_SIZE_PX) * GRID_SIZE_PX;
    const maxX = Math.ceil(rawMaxX / GRID_SIZE_PX) * GRID_SIZE_PX;
    const maxY = Math.ceil(rawMaxY / GRID_SIZE_PX) * GRID_SIZE_PX;

    return {
      left: minX,
      top: minY,
      width: maxX - minX,
      height: maxY - minY,
      labelTop: 0
    };
  }, [groupBlocks, blockDimensions]);

  // PointerEvent fungerar både för mus och touch — onMouseDown
  // suppress:as på mobil när canvas-inner har touch-action:none, vilket
  // gjorde att tap på header inte selecterade gruppen och drag inte
  // startade. Pointer-events fyrar oavsett. React.PointerEvent är
  // strukturellt kompatibel med React.MouseEvent (clientX/Y, target etc.)
  // så onDragStart-callback:en kan ta emot den oförändrat.
  const handleHeaderPointerDown = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
    onClick?.();
    onDragStart?.(e);
  }, [onClick, onDragStart]);

  if (!bounds) return null;

  const displayName = group.name || 'Grupp';

  return (
    <div
      role="group"
      aria-label={displayName}
      data-guide="group-frame"
      data-group-frame-id={group.id}
      className={cn(
        "absolute rounded-2xl border-2 transition-shadow transition-colors pointer-events-auto cursor-pointer",
        // Fade-in vid mount — uppstår vid expand av layer-card.
        // prefers-reduced-motion clamps automatiskt.
        "animate-group-frame-in",
        isSelected
          ? "border-solid shadow-lg"
          : "border-dashed"
      )}
      style={{
        left: bounds.left,
        top: bounds.top,
        width: bounds.width,
        height: bounds.height,
        borderColor: group.color,
        backgroundColor: `${group.color}${isSelected ? '15' : '08'}`
      }}
      // onPointerDown istället för onMouseDown — så touch på mobil också
      // selecterar gruppen utan att förlita sig på syntetisk mousedown
      // (suppress:as när canvas-inner har touch-action:none).
      onPointerDown={(e) => {
        // Only treat clicks on the frame body itself as group-select/drag.
        // Inner blocks/headers stop propagation så denna aldrig stjäl
        // deras gest.
        if (e.target !== e.currentTarget) return;
        e.stopPropagation();
        onClick?.();
        onDragStart?.(e);
      }}
    >
      {/* Clickable + draggable label area. Tap selecterar gruppen; press
          + drag flyttar den. GripVertical-ikonen ger en visuell hint för
          drag-handtaget (särskilt viktig på touch där cursor:grab inte
          syns). touch-none så pointer-events inte konkurrerar med
          browserns native scroll-gester. */}
      <div
        className={cn(
          "absolute -top-0 left-0 right-0 h-10 flex items-center gap-2 px-3 cursor-grab rounded-t-xl transition-colors touch-none select-none",
          isSelected ? "bg-opacity-30" : "hover:bg-opacity-20"
        )}
        style={{
          backgroundColor: `${group.color}20`
        }}
        onPointerDown={handleHeaderPointerDown}
      >
        <GripVertical
          className="w-4 h-4 flex-shrink-0 opacity-60"
          style={{ color: group.color }}
          aria-hidden="true"
        />
        <div
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: group.color }}
        />
        <span
          className="text-sm font-medium truncate"
          style={{ color: group.color }}
        >
          {displayName}
        </span>
        {group.locked && (
          <svg 
            className="w-3 h-3 flex-shrink-0 opacity-60" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            style={{ color: group.color }}
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        )}
      </div>
    </div>
  );
});
